import { useState, useEffect } from 'react';
import AnimatedPage from '@/components/AnimatedPage';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Textarea } from '@easyflow/ui-master/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { FileText, Plus, Pencil, Trash2, Star, RefreshCw, Search, Save, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import type { MasterTenantTemplate as TenantTemplate } from '@easyflow/shared';
import { toErr } from '@easyflow/shared';

export default function TemplatesMaster() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [templates, setTemplates] = useState<TenantTemplate[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TenantTemplate | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    config: JSON.stringify({
      extension_range_start: 1000,
      extension_range_end: 1999,
      max_extensions: 100,
      max_trunks: 10,
      default_codec: 'PCMU,PCMA',
      enable_voicemail: true,
      enable_call_recording: false,
      default_timezone: 'Europe/Rome',
    }, null, 2),
    is_default: false,
  });

  useEffect(() => {
    void loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get<TenantTemplate[]>('/master/templates');
      setTemplates(Array.isArray(response.data) ? response.data : []);
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: 'Error',
        description: __error.message || 'Failed to load templates',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      config: JSON.stringify({
        extension_range_start: 1000,
        extension_range_end: 1999,
        max_extensions: 100,
        max_trunks: 10,
        default_codec: 'PCMU,PCMA',
        enable_voicemail: true,
        enable_call_recording: false,
        default_timezone: 'Europe/Rome',
      }, null, 2),
      is_default: false,
    });
  };

  const handleEdit = (template: TenantTemplate) => {
    setEditingTemplate(template);
    setIsCreating(false);
    setFormData({
      name: template.name,
      description: template.description || '',
      config: JSON.stringify(template.config, null, 2),
      is_default: template.is_default,
    });
  };

  const handleSave = async () => {
    try {
      let config;
      try {
        config = JSON.parse(formData.config);
      } catch (e) {
        toast({
          title: 'Error',
          description: 'Invalid JSON in configuration',
          variant: 'destructive',
        });
        return;
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        config,
        is_default: formData.is_default,
      };

      if (editingTemplate) {
        await apiClient.put(`/master/templates/${editingTemplate.id}`, payload);
        toast({
          title: 'Success',
          description: 'Template updated successfully',
        });
      } else {
        await apiClient.post('/master/templates', payload);
        toast({
          title: 'Success',
          description: 'Template created successfully',
        });
      }

      setIsCreating(false);
      setEditingTemplate(null);
      void loadTemplates();
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: 'Error',
        description: __error.message || 'Failed to save template',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await apiClient.delete(`/master/templates/${templateId}`);
      toast({
        title: 'Success',
        description: 'Template deleted successfully',
      });
      void loadTemplates();
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: 'Error',
        description: __error.message || 'Failed to delete template',
        variant: 'destructive',
      });
    }
  };

  const filteredTemplates = templates.filter(
    (t) =>
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.description && t.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="mb-6">
          <div className="h-10 w-96 bg-white/20 rounded animate-pulse mb-2" />
          <div className="h-4 w-128 bg-white/10 rounded animate-pulse" />
        </div>
        <SkeletonTable rows={8} />
      </div>
    );
  }

  return (
    <AnimatedPage>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Tenant Templates
            </h1>
            <p className="text-muted-foreground mt-1">Predefined configurations for new tenants</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadTemplates} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Button>
          </div>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingTemplate) && (
          <GlassCard className="border-blue-200 bg-blue-50/50 dark:bg-blue-900/20">
            <GlassCardHeader>
              <GlassCardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Template Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Small Business, Enterprise, Call Center"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Description</label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this template"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Configuration (JSON) *</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                      className="rounded border-border"
                    />
                    <span className="text-sm font-medium flex items-center gap-1">
                      <Star className="h-3 w-3 text-yellow-500" />
                      Set as default
                    </span>
                  </label>
                </div>
                <Textarea
                  value={formData.config}
                  onChange={(e) => setFormData({ ...formData, config: e.target.value })}
                  rows={12}
                  className="font-mono text-xs"
                  placeholder='{ "extension_range_start": 1000, ... }'
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Valid JSON configuration for tenant defaults
                </p>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSave}>
                  <Save className="mr-2 h-4 w-4" />
                  Save Template
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreating(false);
                    setEditingTemplate(null);
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </GlassCardContent>
          </GlassCard>
        )}

        {/* Templates Table */}
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 h-4 w-4" />
                <Input
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Configuration Preview</TableHead>
                    <TableHead className="text-center">Default</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No templates found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTemplates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div className="font-medium">{template.name}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground max-w-xs truncate">
                            {template.description || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs font-mono bg-muted/50 dark:bg-white/5 px-2 py-1 rounded max-w-md truncate">
                            {JSON.stringify(template.config).substring(0, 80)}...
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {template.is_default ? (
                            <Badge variant="default" className="bg-yellow-100 text-yellow-700 border-yellow-300">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground/70">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(template)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(template.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    </AnimatedPage>
  );
}
